import { useState } from "react";
import { ArrowRightLeft, Check, Calendar as CalendarIcon, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import AirportInput from "../fligths/AirportInput";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import PassengerSelector from "../fligths/PassengerSelector";
import { MULTICITY, ROUND_TRIP, TERMINAL_ID, TRIP_TYPE_OPTIONS, URL_FLIGHTS, URL_PRINCIPAL } from "@/constants/EngineConstants";
import { MulticitySegment } from "@/types/multicitySegment";
import { v4 as uuidv4 } from "uuid";

const createEmptySegment = (): MulticitySegment => ({
  id: uuidv4(),
  origin: null,
  destination: null,
  date: undefined,
});

const EngineFligths = () => {
    const [tripType, setTripType] = useState(ROUND_TRIP);
    const [origin, setOrigin] = useState<any>(null);
    const [destination, setDestination] = useState<any>(null);
    const [departureDate, setDepartureDate] = useState<Date | undefined>(undefined);
    const [returnDate, setReturnDate] = useState<Date | undefined>(undefined);
    const [departureDateOpen, setDepartureDateOpen] = useState(false);
    const [returnDateOpen, setReturnDateOpen] = useState(false);
    const [passengers, setPassengers] = useState({ adults: 1, children: 0, infants: 0 });

    const formatDateForUrl = (date: Date) => format(date, "yyyy-MM-dd");
    
    // Multicity state
    const [multicitySegments, setMulticitySegments] = useState<MulticitySegment[]>([
        createEmptySegment()
    ]);
    const [multicityDateOpen, setMulticityDateOpen] = useState<Record<string, boolean>>({});

    // Multicity handlers
    const handleAddSegment = () => {
        setMulticitySegments(prev => [...prev, createEmptySegment()]);
    };
    
    const handleRemoveSegment = (id: string) => {
        setMulticitySegments(prev => prev.filter(segment => segment.id !== id));
        setMulticityErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[id];
            return newErrors;
        });
    };

    const handleSearch = () => {
        if (!validateForm()) {
            return;
        }

        // ============================
        // PATH STRING
        // ============================
        const pathParts: string[] = [];
        
        if (tripType == MULTICITY) {
            pathParts.push(multicitySegments.map(x => x.origin.code).toString());
            pathParts.push(multicitySegments.map(x => x.destination.code).toString());
            pathParts.push(multicitySegments.map(x => formatDateForUrl(x.date)).toString());
        } else {
            pathParts.push(origin.code);
            pathParts.push(destination.code);

            if (tripType === ROUND_TRIP) {
                pathParts.push(formatDateForUrl(departureDate));
                pathParts.push(formatDateForUrl(returnDate!));
            } else {
                pathParts.push(formatDateForUrl(departureDate));
            }
        }

        pathParts.push(String(passengers.adults));
        pathParts.push(String(passengers.children));
        pathParts.push(String(passengers.infants));

        const searchPath = pathParts.join("/");

        // ============================
        // QUERY STRING
        // ============================
        const queryParams = new URLSearchParams();
        queryParams.append("terminalId", TERMINAL_ID);

        const queryString = queryParams.toString();

        // ============================
        // URL FINAL
        // ============================
        const finalUrl = `https://${URL_FLIGHTS}/${searchPath}?${queryString}`;

        window.gtag?.("event", "click_open_blank", {
          link_url: finalUrl,
          link_domain: URL_PRINCIPAL,
          link_path: `/vuelos/${searchPath}`
        });
        
        window.open(finalUrl, "_blank");
    };

    const handleSwapLocations = () => {
        if (!origin && !destination) return;

        setOrigin(destination);
        setDestination(origin);
    };

    const updateSegment = (id: string, field: keyof MulticitySegment, value: any) => {
        setMulticitySegments(prev =>
            prev.map(segment =>
                segment.id === id ? { ...segment, [field]: value } : segment
            )
        );
        // Clear error for that field
        if (multicityErrors[id]?.[field as keyof typeof multicityErrors[string]]) {
            setMulticityErrors(prev => ({
                ...prev,
                [id]: { ...prev[id], [field]: undefined }
            }));
        }
    };

    // Validate
    const validateForm = (): boolean => {
    if (tripType === MULTICITY) {
        const newMulticityErrors: typeof multicityErrors = {};
        
        multicitySegments.forEach((segment) => {
        const segmentErrors: { origin?: string; destination?: string; date?: string } = {};
        
        if (!segment.origin) {
            segmentErrors.origin = "El origen es obligatorio";
        }
        if (!segment.destination) {
            segmentErrors.destination = "El destino es obligatorio";
        }
        if (!segment.date) {
            segmentErrors.date = "La fecha es obligatoria";
        }
        
        if (Object.keys(segmentErrors).length > 0) {
            newMulticityErrors[segment.id] = segmentErrors;
        }
        });
        
        setMulticityErrors(newMulticityErrors);
        return Object.keys(newMulticityErrors).length === 0;
    }

    const newErrors: typeof errors = {};

    if (!origin) {
        newErrors.origin = "El origen es obligatorio";
    }

    if (!destination) {
        newErrors.destination = "El destino es obligatorio";
    }

    if (!departureDate) {
        newErrors.departureDate = "La fecha de ida es obligatoria";
    }

    if (tripType === ROUND_TRIP && !returnDate) {
        newErrors.returnDate = "La fecha de vuelta es obligatoria";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
    };

    // Multicity errors
    const [multicityErrors, setMulticityErrors] = useState<Record<string, {
    origin?: string;
    destination?: string;
    date?: string;
    }>>({});


    // Validation errors
    const [errors, setErrors] = useState<{
    origin?: string;
    destination?: string;
    departureDate?: string;
    returnDate?: string;
    }>({});

    return (
        <>
          {/* Header */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-3 md:mb-6">
            <h2 className="text-xl md:text-2xl font-semibold text-foreground">
              ¿A dónde quieres ir?
            </h2>
            <div className="grid grid-cols-2 md:flex md:flex-row md:justify-end gap-2 w-full md:w-auto transition-all duration-300">
              {TRIP_TYPE_OPTIONS.map((option, index) => (
                <button
                  key={option.type}
                  onClick={() => setTripType(option.type)}
                  className={cn(
                    "flex items-center justify-center gap-2 px-4 py-2 h-9 lg:h-10 rounded-full border-2 text-sm font-medium transition-all",
                    tripType === option.type
                      ? "border-secondary text-secondary"
                      : "border-border text-muted-foreground hover:border-secondary/50",
                    // Last item (Múltiples destinos) spans full width on mobile
                    index === TRIP_TYPE_OPTIONS.length - 1 && "col-span-2 md:col-span-1"
                  )}
                >
                  {tripType === option.type && <Check className="w-4 h-4" />}
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Search Fields */}
          {tripType === MULTICITY ? (
            /* Multicity UI */
            <div className="space-y-4 mb-4">
              {multicitySegments.map((segment, index) => (
                <div key={segment.id} className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-foreground">Tramo #{index + 1}</span>
                    {index > 0 && (
                      <button
                        onClick={() => handleRemoveSegment(segment.id)}
                        className="p-1 rounded-full hover:bg-destructive/10 text-destructive transition-colors"
                        title="Eliminar tramo"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Origin */}
                    <div>
                      <AirportInput
                        placeholder="Ingresa un origen"
                        identifier={`multicity-origin-${segment.id}`}
                        value={segment.origin}
                        onSelectAirport={({ location }) => updateSegment(segment.id, 'origin', location)}
                      />
                      <div className="md:h-5">
                        {multicityErrors[segment.id]?.origin && (
                          <span className="text-destructive text-xs">{multicityErrors[segment.id].origin}</span>
                        )}
                      </div>
                    </div>
                    {/* Destination */}
                    <div>
                      <AirportInput
                        placeholder="Ingresa un destino"
                        identifier={`multicity-dest-${segment.id}`}
                        value={segment.destination}
                        onSelectAirport={({ location }) => updateSegment(segment.id, 'destination', location)}
                      />
                      <div className="md:h-5">
                        {multicityErrors[segment.id]?.destination && (
                          <span className="text-destructive text-xs">{multicityErrors[segment.id].destination}</span>
                        )}
                      </div>
                    </div>
                    {/* Date */}
                    <div>
                      <Popover
                        open={multicityDateOpen[segment.id] || false}
                        onOpenChange={(open) => setMulticityDateOpen(prev => ({ ...prev, [segment.id]: open }))}
                      >
                        <PopoverTrigger asChild>
                          <button
                            className={cn(
                              "relative w-full px-4 py-3 pl-10 border-2 rounded-lg focus:border-secondary focus:outline-none transition-colors bg-background text-left",
                              multicityErrors[segment.id]?.date ? "border-destructive" : "border-border",
                              segment.date ? "text-foreground" : "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            {segment.date ? format(segment.date, "dd MMM yyyy", { locale: es }) : "Fecha de vuelo"}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={segment.date}
                            onSelect={(date) => {
                              updateSegment(segment.id, 'date', date);
                              setMulticityDateOpen(prev => ({ ...prev, [segment.id]: false }));
                            }}
                            disabled={(date) => {
                              const today = new Date(new Date().setHours(0, 0, 0, 0));
                              // Find previous segment's date for min date
                              const prevSegment = multicitySegments[index - 1];
                              const minDate = prevSegment?.date || today;
                              return date < minDate;
                            }}
                            initialFocus
                            locale={es}
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                      <div className="md:h-5">
                        {multicityErrors[segment.id]?.date && (
                          <span className="text-destructive text-xs">{multicityErrors[segment.id].date}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Add segment button */}
              <button
                onClick={handleAddSegment}
                className="flex items-center gap-2 text-secondary hover:text-secondary/80 font-medium transition-colors"
              >
                <Plus className="w-4 h-4" />
                Agregar tramo
              </button>

              {/* Passengers for multicity */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <div className="relative">
                  <label className="block text-sm font-semibold text-foreground mb-1">Pasajeros</label>
                  <PassengerSelector value={passengers} onChange={setPassengers} />
                </div>
              </div>
            </div>
          ) : (
            /* Regular UI (Round trip / One way) */
            <>
              <div className="grid grid-cols-1 md:grid-cols-[1fr,auto,1fr] gap-0 md:gap-6">
                {/* From */}
                <div className="relative mb-2 lg:mp-0">
                  <label className="block text-sm font-semibold text-foreground mb-1">Desde</label>
                  <AirportInput
                    placeholder="Ingresa un origen"
                    identifier="origin"
                    value={origin}
                    onSelectAirport={({ location }) => {
                      setOrigin(location);
                      if (errors.origin) setErrors(prev => ({ ...prev, origin: undefined }));
                    }}
                  />
                  <div className="md:h-5 mt-0">
                    {errors.origin && (
                      <span className="text-destructive text-xs">{errors.origin}</span>
                    )}
                  </div>
                </div>

                {/* Swap Button */}
                <div className="flex items-center justify-center">
                  <button onClick={handleSwapLocations} className="w-12 h-7 lg:w-10 lg:h-10 rounded-full border-2 border-secondary flex items-center justify-center hover:bg-secondary/10 transition-colors" aria-label="Intercambiar origen y destino">
                    <ArrowRightLeft className="w-4 h-4 text-secondary" />
                  </button>
                </div>

                {/* To */}
                <div className="relative mb-2 lg:mp-0">
                  <label className="block text-sm font-semibold text-foreground mb-1">Hacia</label>
                  <AirportInput
                    placeholder="Ingresa un destino"
                    identifier="destino"
                    value={destination}
                    onSelectAirport={({ location }) => {
                      setDestination(location);
                      if (errors.destination) setErrors(prev => ({ ...prev, destination: undefined }));
                    }}
                  />
                  <div className="md:h-5 mt-0">
                    {errors.destination && (
                      <span className="text-destructive text-xs">{errors.destination}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Date and Passengers */}
              <div className="grid grid-cols-1 grid-cols-2 md:grid-cols-3 gap-1 lg:gap-4 mb-3 lg:mb-1">
                {/* Departure Date */}
                <div className="relative mb-1 lg:mb-0">
                  <label className="block text-sm font-semibold text-foreground mb-1">Fecha de ida</label>
                  <Popover open={departureDateOpen} onOpenChange={setDepartureDateOpen}>
                    <PopoverTrigger asChild>
                      <button
                        className={cn(
                          "relative w-full px-2 py-3 pl-9 border-2 rounded-lg focus:border-secondary focus:outline-none transition-colors bg-background text-left",
                          errors.departureDate ? "border-destructive" : "border-border",
                          departureDate ? "text-foreground" : "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <span className="block truncate">{departureDate ? format(departureDate, "dd MMM yyyy", { locale: es }) : "Selecciona fecha"}</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={departureDate}
                        onSelect={(date) => {
                          setDepartureDate(date);
                          setDepartureDateOpen(false);
                          if (errors.departureDate) setErrors(prev => ({ ...prev, departureDate: undefined }));
                          // Clear return date if it's before the new departure date
                          if (date && returnDate && returnDate < date) {
                            setReturnDate(undefined);
                          }
                          // Auto-open return date picker for round trips
                          if (tripType === ROUND_TRIP) {
                            setTimeout(() => setReturnDateOpen(true), 150);
                          }
                        }}
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                        locale={es}
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                  <div className="md:h-5 mt-0">
                    {errors.departureDate && (
                      <span className="text-destructive text-xs">{errors.departureDate}</span>
                    )}
                  </div>
                </div>

                {/* Return Date */}
                {tripType === ROUND_TRIP && (
                  <div className="relative mb-1 lg:mb-0">
                    <label className="block text-sm font-semibold text-foreground mb-1">Fecha de vuelta</label>
                    <Popover open={returnDateOpen} onOpenChange={setReturnDateOpen}>
                      <PopoverTrigger asChild>
                        <button
                          className={cn(
                            "relative w-full px-2 py-3 pl-9 border-2 rounded-lg focus:border-secondary focus:outline-none transition-colors bg-background text-left",
                            errors.returnDate ? "border-destructive" : "border-border",
                            returnDate ? "text-foreground" : "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <span className="block truncate">{returnDate ? format(returnDate, "dd MMM yyyy", { locale: es }) : "Selecciona fecha"}</span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={returnDate}
                          onSelect={(date) => {
                            setReturnDate(date);
                            setReturnDateOpen(false);
                            if (errors.returnDate) setErrors(prev => ({ ...prev, returnDate: undefined }));
                          }}
                          disabled={(date) => {
                            const today = new Date(new Date().setHours(0, 0, 0, 0));
                            const minDate = departureDate || today;
                            return date < minDate;
                          }}
                          defaultMonth={departureDate}
                          initialFocus
                          locale={es}
                          className="p-3 pointer-events-auto"
                          modifiers={{
                            range_start: departureDate ? [departureDate] : [],
                            range_end: returnDate ? [returnDate] : [],
                            range_middle: departureDate && returnDate
                              ? { after: departureDate, before: returnDate }
                              : undefined,
                          }}
                          modifiersClassNames={{
                            range_start: "bg-secondary text-secondary-foreground rounded-l-md",
                            range_end: "bg-secondary text-secondary-foreground rounded-r-md",
                            range_middle: "bg-secondary/20 text-foreground",
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                    <div className="md:h-5 mt-0">
                      {errors.returnDate && (
                        <span className="text-destructive text-xs">{errors.returnDate}</span>
                      )}
                    </div>
                  </div>
                )}

                <div className="relative flex flex-col lg:col-span-1 col-span-2">
                  <label className="block text-sm font-semibold text-foreground mb-1">Pasajeros</label>
                  <PassengerSelector value={passengers} onChange={setPassengers} />
                  <div className="md:h-5 mt-0"></div>
                </div>
              </div>
            </>
          )}

          {/* Search Button */}
          <Button onClick={handleSearch} className="w-full sm:w-auto bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold py-6 px-8 rounded-full text-base shadow-glow">
            Buscar vuelos
          </Button>
            </>
    )
}

export default EngineFligths;